/* Grid toolbar: date window controls, Mark Today, template/export/import
   (XLSX round-trip) + the import summary dialog. */
import { useRef, useState } from 'react';
import { CalendarCheck, ChevronLeft, ChevronRight, Download, FileSpreadsheet, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../api/client';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import { TraineeFilterBar } from '../TraineeFilterContext';

export const AttendanceToolbar = ({
  canEdit, hasRows, activeStatuses,
  startDate, endDate, setStartDate, setEndDate, setWindowDays, shiftWindow,
  markAllToday, scopeQS, onImported,
}) => {
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const fileQuery = () => {
    const params = new URLSearchParams();
    const scope = scopeQS();
    if (scope) { const [k, v] = scope.split('='); params.append(k, decodeURIComponent(v)); }
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return params.toString();
  };

  const downloadXlsx = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed');
    }
  };

  const onDownloadTemplate = () => {
    const qs = fileQuery();
    downloadXlsx(`/trainees/attendance/bulk/template${qs ? `?${qs}` : ''}`,
      `attendance-template-${startDate}_to_${endDate}.xlsx`);
    toast.success('Template downloading…');
  };

  const onExport = () => {
    const qs = fileQuery();
    downloadXlsx(`/trainees/attendance/bulk/export${qs ? `?${qs}` : ''}`,
      `attendance-${startDate}_to_${endDate}.xlsx`);
    toast.success('Export downloading…');
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/trainees/attendance/bulk/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      toast.success(`Imported ${res.data.created} marks (${res.data.updated} updated)`);
      await onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setSaving(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2 items-end">
        <TraineeFilterBar />
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={startDate}
                 onChange={e => setStartDate(e.target.value)}
                 className="w-40" data-testid="attendance-start-date" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={endDate}
                 onChange={e => setEndDate(e.target.value)}
                 className="w-40" data-testid="attendance-end-date" />
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={() => setWindowDays(7)}>7d</Button>
          <Button size="sm" variant="outline" onClick={() => setWindowDays(14)}>14d</Button>
          <Button size="sm" variant="outline" onClick={() => setWindowDays(30)}>30d</Button>
        </div>
        <div className="flex gap-1">
          <Button size="icon" variant="outline" onClick={() => shiftWindow(-7)} aria-label="Previous week">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="outline" onClick={() => shiftWindow(7)} aria-label="Next week">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="ml-auto flex gap-2">
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" hidden
                 onChange={onImport} data-testid="attendance-import-input" />
          {canEdit && (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" disabled={saving || !hasRows}
                          data-testid="mark-today-btn">
                    <CalendarCheck className="w-4 h-4 mr-2" /> Mark Today
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {activeStatuses.map(s => (
                    <DropdownMenuItem key={s.code} onClick={() => markAllToday(s.code)}
                                      data-testid={`mark-today-${s.code}`}>
                      <span className={`inline-block w-4 h-4 rounded text-[9px] font-semibold flex items-center justify-center mr-2 ${s.color}`}>
                        {s.short_code}
                      </span>
                      Mark all {s.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" onClick={onDownloadTemplate}
                      disabled={saving} data-testid="attendance-template-btn"
                      title="Download a blank attendance template for the current window">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Template
              </Button>
              <Button variant="outline" onClick={onExport}
                      disabled={saving} data-testid="attendance-export-btn"
                      title="Download current grid as XLSX (round-trippable)">
                <Download className="w-4 h-4 mr-2" /> Export
              </Button>
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}
                      disabled={saving} data-testid="attendance-import-btn">
                <Upload className="w-4 h-4 mr-2" /> Import
              </Button>
            </>
          )}
        </div>
      </div>

      <Dialog open={!!importResult} onOpenChange={(o) => !o && setImportResult(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Attendance Import Summary</DialogTitle></DialogHeader>
          {importResult && (
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <Card className="p-3 text-center">
                  <p className="text-xs text-muted-foreground">Created</p>
                  <p className="font-display tabular-nums text-2xl font-bold">{importResult.created}</p>
                </Card>
                <Card className="p-3 text-center">
                  <p className="text-xs text-muted-foreground">Updated</p>
                  <p className="font-display tabular-nums text-2xl font-bold">{importResult.updated}</p>
                </Card>
                <Card className="p-3 text-center">
                  <p className="text-xs text-muted-foreground">Skipped</p>
                  <p className="font-display tabular-nums text-2xl font-bold">{importResult.skipped}</p>
                </Card>
              </div>
              {importResult.errors?.length > 0 && (
                <div className="max-h-48 overflow-y-auto border rounded p-2 text-xs">
                  {importResult.errors.map((e, i) => (
                    <p key={i} className="text-rose-600">Row {e.row}: {e.message}</p>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Accepts long format (columns: <code>employee_id</code> or <code>name</code>, <code>date</code>, <code>status</code>)
                or wide format (one date per column).
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setImportResult(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
